// Default mode: Fülkit-managed storage via Supabase
// Plaintext notes, encrypted at rest by Supabase
// (Model B / encrypted-sync was removed in v4 addendum execution — see md/archive/model-b-v2.md)

export async function readFulkitNotes(supabase, userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from("notes")
    .select("id, title, content, pinned, context_mode, source, folder, updated_at")
    .eq("user_id", userId)
    .neq("encrypted", true)  // legacy column — kept until DB cleanup migration
    .order("pinned", { ascending: false })
    .order("updated_at", { ascending: false })
    .abortSignal(AbortSignal.timeout(5000));

  if (error) {
    console.error("[vault-fulkit] Read error:", error.message);
    return [];
  }
  return data || [];
}

export async function importNote({ title, content, source, folder, pinned }, supabase, userId) {
  const isPinned = pinned || false;
  const { data, error } = await supabase
    .from("notes")
    .insert({
      user_id: userId,
      title,
      content,
      source: source || "upload",
      folder: folder || "00-INBOX",
      pinned: isPinned,
      context_mode: isPinned ? "always" : "available",
      encrypted: false,
    })
    .select("id, title")
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// V2 Step 12a — soft delete by default per V2 spec lines 400-453.
// Marks status='deleted' + sets deleted_at=NOW() so the entry is
// recoverable for 30 days. Pulse heat goes to 0 so it stops surfacing
// in active memory immediately. Per V2 spec line 402: "Supabase row
// marked status='deleted' for 30 days, ledger entry marked deleted with
// timestamp, binary kept in storage bucket with valid signed URL.
// Pulse heat goes to 0."
//
// Hard delete reserved for explicit user request only ("Permanently
// delete...") — pass { permanent: true }. Hard-delete is destructive
// and irreversible: row removed, binary removed from storage.
//
// Disk-side file removal is the caller's responsibility (V2 spec line
// 402 says "disk file removed immediately"; settings/page.js + chat
// tool wrappers handle disk cleanup separately via deleteLocalNote).
export async function deleteNote(id, supabase, userId, options = {}) {
  const permanent = Boolean(options.permanent);

  if (permanent) {
    // Hard delete — V2 spec line 437-439. Row gone, binary gone, ledger
    // entry hard-removed (ledger rebuild from Supabase on next bootstrap).
    const query = supabase.from("notes").delete().eq("id", id);
    if (userId) query.eq("user_id", userId);
    const { error } = await query;
    if (error) throw new Error(error.message);
    return { permanent: true, id };
  }

  // Soft delete (default). Per V2 spec: status flip + deleted_at stamp
  // + heat reset. 30-day recovery window enforced at recall query time.
  const updates = {
    status: "deleted",
    deleted_at: new Date().toISOString(),
    // Per V2 spec line 402: "Pulse heat goes to 0." Zero out the
    // heat math counters so a soft-deleted entry stops surfacing
    // in active memory immediately.
    recall_count: 0,
    mention_count: 0,
    drop_count: 0,
  };
  const query = supabase.from("notes").update(updates).eq("id", id);
  if (userId) query.eq("user_id", userId);
  const { error } = await query;
  if (error) throw new Error(error.message);
  return { permanent: false, id, deleted_at: updates.deleted_at };
}

// V2 Step 12a — restore a soft-deleted note per V2 spec lines 426-432.
// Flips status back to 'active', clears deleted_at, returns the row's
// folder + title for the caller to compute restore path. Caller
// handles path collision suffix logic (`-restored-DATE`).
//
// Returns { restored: true, id, title, folder } on success.
// Throws "expired" if entry is past 30-day window (caller emits
// vault_restore_failed_expired and returns IRON-CLAD copy).
// Throws "not_found" if id doesn't exist or isn't soft-deleted.
export async function restoreNote(id, supabase, userId) {
  // Look up the soft-deleted row first to verify status + window.
  const lookup = supabase
    .from("notes")
    .select("id, title, folder, status, deleted_at")
    .eq("id", id);
  if (userId) lookup.eq("user_id", userId);
  const { data: row, error: lookupError } = await lookup.maybeSingle();
  if (lookupError) throw new Error(lookupError.message);
  if (!row) throw new Error("not_found");
  if (row.status !== "deleted") throw new Error("not_deleted");

  // 30-day window check. Per V2 spec line 424: "That's past the 30-day
  // window — I let it go for good." Entries past window stay as
  // status='deleted' rows but become unrecoverable via this path.
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const deletedAt = row.deleted_at ? new Date(row.deleted_at).getTime() : 0;
  if (!deletedAt || (Date.now() - deletedAt) > THIRTY_DAYS_MS) {
    throw new Error("expired");
  }

  const updates = {
    status: "active",
    deleted_at: null,
    updated_at: new Date().toISOString(),
  };
  const flip = supabase.from("notes").update(updates).eq("id", id);
  if (userId) flip.eq("user_id", userId);
  const { error: flipError } = await flip;
  if (flipError) throw new Error(flipError.message);

  return { restored: true, id: row.id, title: row.title, folder: row.folder };
}

export async function getNoteCount(supabase, userId) {
  const query = supabase
    .from("notes")
    .select("id", { count: "exact", head: true });
  if (userId) query.eq("user_id", userId);
  const { count, error } = await query;
  if (error) return 0;
  return count || 0;
}

export async function updateContextMode(id, mode, supabase, userId) {
  const query = supabase
    .from("notes")
    .update({
      context_mode: mode,
      pinned: mode === "always",
    })
    .eq("id", id);
  if (userId) query.eq("user_id", userId);
  const { error } = await query;
  if (error) throw new Error(error.message);
}

export async function searchNotes(query, supabase, userId) {
  const q = query.toLowerCase();
  const dbQuery = supabase
    .from("notes")
    .select("id, title, content, context_mode, source, folder")
    .order("updated_at", { ascending: false });
  if (userId) dbQuery.eq("user_id", userId);
  const { data, error } = await dbQuery;

  if (error) {
    console.error("[vault-fulkit] searchNotes error:", error.message);
    return [];
  }

  return (data || []).filter((n) => {
    const haystack = `${n.title} ${n.folder}`.toLowerCase();
    return haystack.includes(q);
  }).map((n) => ({
    id: n.id,
    title: n.title,
    content: n.content,
    context_mode: n.context_mode,
    folder: n.folder,
    tokenEstimate: n.content ? Math.ceil(n.content.length / 4) : 0,
  }));
}

export async function listNotes(supabase, userId) {
  const query = supabase
    .from("notes")
    .select("id, title, context_mode, source, folder, updated_at, content")
    .order("context_mode", { ascending: true })
    .order("updated_at", { ascending: false });
  if (userId) query.eq("user_id", userId);
  const { data, error } = await query;

  if (error) {
    console.error("[vault-fulkit] listNotes error:", error.message);
    return [];
  }

  return (data || []).map((n) => ({
    id: n.id,
    title: n.title,
    context_mode: n.context_mode,
    source: n.source,
    folder: n.folder,
    updated_at: n.updated_at,
    tokenEstimate: n.content ? Math.ceil(n.content.length / 4) : 0,
  }));
}
